import { DefaultValues } from 'react-hook-form'

import { uniq } from 'lodash-es'

import {
  AddressDetailsDTO,
  BookingDTOCargoFCL,
  ContainerItemDTO,
  CreateBookingDTOTransportType,
  IncotermDTOCode,
  LCLItemDTO,
  WaypointBaseDTOTransportMode,
} from '@/clients/booking'
import { CargoType } from '@/features/bookings/api/cargoTypes'
import { incotermCodeToNamedPlaceOfDelivery } from '@/features/bookings/api/incoterms'
import { HouseCargo, HouseShipment, HouseShipmentAddress } from '@/features/shipments'

import { BookingWizardFormState, LclCargoType } from '../wizard/types/BookingWizardFormState'
import {
  bookingWizardDefaultBooking,
  defaultFCLCargo,
  defaultLCLCargoByDimensionsItem,
  getContainerTypeFromCargoTypes,
  processCommodityData,
} from '../wizard/utils/bookingCreationUtils'

const NODE_TYPE = {
  originPort: 'port-of-loading',
  destinationPort: 'port-of-discharge',
  pickup: 'place-of-pickup',
  delivery: 'place-of-delivery',
} as const

type MapShipmentToBookingWizardFormStateArgs = {
  shipment: HouseShipment
  cargoTypes: CargoType[]
  isMultipleCommoditiesUnNumbersEnabled: boolean
}

// Map shipment modes to a supported booking main-carriage mode (seaAir has no booking
// equivalent, so it falls back to air); unknown values resolve to undefined.
const SHIPMENT_TO_MAIN_CARRIAGE_MODE: Record<string, WaypointBaseDTOTransportMode> = {
  sea: WaypointBaseDTOTransportMode.Sea,
  air: WaypointBaseDTOTransportMode.Air,
  rail: WaypointBaseDTOTransportMode.Rail,
  seaAir: WaypointBaseDTOTransportMode.Air,
}

const toMainCarriageMode = (mode: string): WaypointBaseDTOTransportMode | undefined =>
  SHIPMENT_TO_MAIN_CARRIAGE_MODE[mode]

const toAddressDetails = (address?: HouseShipmentAddress): AddressDetailsDTO | undefined => {
  if (!address) {
    return undefined
  }
  return {
    addressID: address.addressID,
    locationName: address.companyName,
    place: {
      street: address.street,
      streetNumber: address.streetNumber,
      postalCode: address.postalCode,
      city: address.city,
      state: address.state,
      country: address.country,
      countryCode: address.countryCode,
    },
  }
}

const getPortId = (shipment: HouseShipment, nodeType: string): string | undefined =>
  shipment.nodes.find((node) => node.type === nodeType)?.transportPoint.port?.ID

const getAddress = (shipment: HouseShipment, nodeType: string): AddressDetailsDTO | undefined =>
  toAddressDetails(shipment.nodes.find((node) => node.type === nodeType)?.transportPoint.address)

// One HouseCargo represents one physical container; group by unit code to derive quantities.
const buildFclContainers = (houseCargos: HouseCargo[], cargoTypes: CargoType[]): ContainerItemDTO[] => {
  const countByUnitCode = new Map<string, number>()
  houseCargos.forEach((cargo) => {
    if (cargo.transportUnitCode) {
      countByUnitCode.set(cargo.transportUnitCode, (countByUnitCode.get(cargo.transportUnitCode) ?? 0) + 1)
    }
  })

  return Array.from(countByUnitCode.entries())
    .map(([unitCode, quantity]) => ({
      type: getContainerTypeFromCargoTypes(cargoTypes, unitCode),
      quantity,
    }))
    .filter((container): container is ContainerItemDTO => container.type != null)
}

const buildLclItems = (houseCargos: HouseCargo[]): LCLItemDTO[] =>
  houseCargos.flatMap((cargo) =>
    cargo.cargoItems.map((item) => ({
      quantity: item.numberOfPackages ?? 0,
      weight: item.weightInKg ?? 0,
      volume: item.packageDimensions?.volumeInCubicMeters ?? 0,
      dimensions: {
        lengthCm: item.packageDimensions?.lengthInCM ?? 0,
        widthCm: item.packageDimensions?.widthInCM ?? 0,
        heightCm: item.packageDimensions?.heightInCM ?? 0,
      },
    }))
  )

const getCommodityData = (houseCargos: HouseCargo[], isMultipleCommoditiesUnNumbersEnabled: boolean) => {
  const commodities = houseCargos
    .flatMap((cargo) => cargo.commodities ?? [])
    .filter((commodity) => commodity.id != null && commodity.description != null)
    .map((commodity) => ({ commodity: commodity.description as string, commodityID: commodity.id as string }))

  const first = houseCargos.find((cargo) => cargo.commodityID != null)

  return processCommodityData({
    commodities,
    commodity: first?.commodityDescription,
    commodityID: first?.commodityID,
    isMultipleCommoditiesUnNumbersEnabled,
  })
}

const isKnownIncotermCode = (code: string): code is IncotermDTOCode =>
  (Object.values(IncotermDTOCode) as string[]).includes(code)

// Split a stored incoterm string (e.g. "FCA_LW") into the DTO's code + namedPlaceOfDelivery.
// Unrecognised values fall back to the default so the wizard prompts for a valid incoterm.
const toIncoterm = (incoterms?: string) => {
  if (!incoterms) {
    return bookingWizardDefaultBooking.incoterm
  }
  const namedPlaceOfDelivery =
    incotermCodeToNamedPlaceOfDelivery[incoterms as keyof typeof incotermCodeToNamedPlaceOfDelivery]
  if (namedPlaceOfDelivery) {
    return { code: IncotermDTOCode.Fca, namedPlaceOfDelivery }
  }
  if (isKnownIncotermCode(incoterms)) {
    return { code: incoterms }
  }
  return bookingWizardDefaultBooking.incoterm
}

export const mapShipmentToBookingWizardFormState = ({
  shipment,
  cargoTypes,
  isMultipleCommoditiesUnNumbersEnabled,
}: MapShipmentToBookingWizardFormStateArgs): DefaultValues<BookingWizardFormState> => {
  const { details, contacts, houseCargos } = shipment

  const transportType =
    details.mainTransportType === 'lcl' ? CreateBookingDTOTransportType.Lcl : CreateBookingDTOTransportType.Fcl
  const isFCL = transportType === CreateBookingDTOTransportType.Fcl

  const mainCarriageMode = toMainCarriageMode(details.mainTransportMode)
  const commodityData = getCommodityData(houseCargos, isMultipleCommoditiesUnNumbersEnabled)

  const containers = buildFclContainers(houseCargos, cargoTypes)
  const cargoFCL: DefaultValues<BookingDTOCargoFCL> | undefined = isFCL
    ? {
        ...defaultFCLCargo,
        // Fall back to the default empty container row when no container has a resolvable type,
        // so the form always shows a container to complete instead of a broken empty section.
        containers: containers.length ? containers : defaultFCLCargo.containers,
        ...commodityData,
      }
    : undefined

  const cargoLCL: Pick<BookingWizardFormState, 'cargoLCL' | 'cargoLCLByDimensionsItems'> | undefined = !isFCL
    ? {
        cargoLCL: {
          ...commodityData,
          isStackableGoods: houseCargos.some((cargo) => cargo.isStackable),
        },
        cargoLCLByDimensionsItems: buildLclItems(houseCargos).length
          ? buildLclItems(houseCargos)
          : [defaultLCLCargoByDimensionsItem as LCLItemDTO],
      }
    : undefined

  const unNumbers = uniq(houseCargos.flatMap((cargo) => cargo.unNumbers))
  const hasDangerousGoods = unNumbers.length > 0 || shipment.services?.dangerousGoodsService != null

  return {
    ...bookingWizardDefaultBooking,
    // Link the new booking to the shipment's originating booking when one exists.
    clonedFrom: details.bookingID,
    transportType,
    mainCarriageMode: mainCarriageMode ? [mainCarriageMode] : [],
    originPort: getPortId(shipment, NODE_TYPE.originPort),
    destinationPort: getPortId(shipment, NODE_TYPE.destinationPort),
    pickupAddressDetails: getAddress(shipment, NODE_TYPE.pickup),
    deliveryAddressDetails: getAddress(shipment, NODE_TYPE.delivery),
    cargoFCL,
    ...cargoLCL,
    lclCargoType: LclCargoType.ByDimensions,
    incoterm: toIncoterm(details.incoterms),
    shipperLegalEntityID: details.shipperLegalEntityID,
    consigneeLegalEntityID: details.consigneeLegalEntityID,
    onBehalfOfShipperLegalEntityID: details.onBehalfOfShipperLegalEntityID,
    onBehalfOfConsigneeLegalEntityID: details.onBehalfOfConsigneeLegalEntityID,
    shipperContactID: contacts.shipperContactID,
    consigneeContactID: contacts.consigneeContactID,
    onBehalfOfShipperContactID: contacts.onBehalfOfShipperContactID,
    onBehalfOfConsigneeContactID: contacts.onBehalfOfConsigneeContactID,
    shipper: contacts.shipperContact,
    consignee: contacts.consigneeContact,
    shipperLegalEntityCompanyName: contacts.shipperContact?.companyName,
    consigneeLegalEntityCompanyName: contacts.consigneeContact?.companyName,
    hasDangerousGoods,
    unNumbers,
  }
}
